import React, { useState, useEffect, useCallback } from 'react';
import {
  Container, Button, Box, Paper, Typography, Chip, TextField, Stack,
  useMediaQuery, useTheme, Select, MenuItem, FormControl, InputLabel
} from '@mui/material';
import { PointOfSale, CalendarToday } from '@mui/icons-material';
import { MenuInicial } from './MenuInicial';
import { useNavigate } from 'react-router-dom';
import { API_BASE_URL, TURNOS_LISTAR, TURNO_FORMDATA } from '../services/apiConfig';
import { DataGrid } from '@mui/x-data-grid';
import Cargando from './utils/Cargando.jsx';
import TituloPagina from './utils/TituloPagina.jsx';
import ExportToolbar from './utils/ExportToolbar.jsx';
import axios from 'axios';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import './DatePickerStyles.css';

const authHeader = () => {
  const t = sessionStorage.getItem('authToken');
  return t ? { Authorization: `Bearer ${t}` } : {};
};

const toYmd = (d) => {
  const x = new Date(d);
  return `${x.getFullYear()}-${String(x.getMonth() + 1).padStart(2, '0')}-${String(x.getDate()).padStart(2, '0')}`;
};

const fmtMoneda = (n) => n == null ? '—' : `$${Number(n).toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtFecha = (f) => {
  if (!f) return '—';
  const d = new Date(String(f).replace(' ', 'T'));
  if (isNaN(d)) return String(f);
  return `${d.toLocaleDateString('es-AR')} ${d.toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit', hour12: false })}`;
};

function TurnosList() {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [loading, setLoading] = useState(true);
  const [turnos, setTurnos] = useState([]);
  const [startDate, setStartDate] = useState(() => { const d = new Date(); d.setDate(d.getDate() - 7); return d; });
  const [endDate, setEndDate] = useState(new Date());
  const [sucursalId, setSucursalId] = useState('');
  const [sucursales, setSucursales] = useState([]);
  const perfil = Number(JSON.parse(sessionStorage.getItem('userData') || '{}')?.perfil);

  // Combo de sucursal: habilitado solo para perfil 1 (admin) y 2 (franquiciado)
  // -- perfil 3 solo tiene una sucursal, no lo necesita (mismo criterio que
  // el resto del sistema, ver Analisis.jsx / RegistrosProductosList.jsx).
  // Reusa TURNO_FORMDATA, que ya trae las sucursales scopeadas por perfil
  // (sucursalesParaFiltro) para la pantalla de abrir/cerrar turno -- no hace
  // falta un endpoint nuevo solo para este combo.
  useEffect(() => {
    if (perfil !== 1 && perfil !== 2) return;
    axios.get(API_BASE_URL + TURNO_FORMDATA, { headers: authHeader() })
      .then((res) => {
        const payload = res.data?.data ?? res.data;
        setSucursales(Array.isArray(payload?.sucursales) ? payload.sucursales : []);
      })
      .catch(() => setSucursales([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchTurnos = useCallback(() => {
    setLoading(true);
    axios.get(API_BASE_URL + TURNOS_LISTAR, {
      params: { startDate: toYmd(startDate), endDate: toYmd(endDate), sucursal_id: sucursalId || undefined },
      headers: authHeader(),
    })
      .then((res) => {
        const payload = res.data?.data ?? res.data;
        setTurnos(Array.isArray(payload) ? payload : []);
      })
      .catch(() => setTurnos([]))
      .finally(() => setLoading(false));
  }, [startDate, endDate, sucursalId]);

  useEffect(() => { fetchTurnos(); }, [fetchTurnos]);

  const columns = [
    { field: 'sucursal', headerName: 'Sucursal', flex: 1, minWidth: 130 },
    { field: 'fecha_apertura', headerName: 'Apertura', width: 150, valueGetter: (p) => fmtFecha(p.row.fecha_apertura) },
    { field: 'usuario_apertura', headerName: 'Abrió', width: 120 },
    { field: 'fecha_cierre', headerName: 'Cierre', width: 150, valueGetter: (p) => fmtFecha(p.row.fecha_cierre) },
    ...(perfil !== 3 ? [{ field: 'total_ventas', headerName: 'Registrado', width: 120, renderCell: (p) => fmtMoneda(p.row.total_ventas) }] : []),
    {
      field: 'estado', headerName: 'Estado', width: 110,
      renderCell: (p) => <Chip size="small" label={p.row.estado === 'abierto' ? 'Abierto' : 'Cerrado'} color={p.row.estado === 'abierto' ? 'success' : 'default'} />,
    },
  ];

  const filtros = (
    <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
      {isMobile ? (
        <>
          <DatePicker selected={startDate} onChange={setStartDate} dateFormat="dd/MM/yyyy"
            customInput={<TextField size="small" fullWidth InputProps={{ startAdornment: <CalendarToday sx={{ mr: 1, fontSize: '1rem', color: 'text.secondary' }} /> }} />} />
          <DatePicker selected={endDate} onChange={setEndDate} dateFormat="dd/MM/yyyy"
            customInput={<TextField size="small" fullWidth InputProps={{ startAdornment: <CalendarToday sx={{ mr: 1, fontSize: '1rem', color: 'text.secondary' }} /> }} />} />
        </>
      ) : (
        <>
          <DatePicker selected={startDate} onChange={setStartDate} selectsStart startDate={startDate} endDate={endDate} dateFormat="dd/MM/yyyy"
            customInput={<TextField label="Fecha Desde" size="small" />} />
          <DatePicker selected={endDate} onChange={setEndDate} selectsEnd startDate={startDate} endDate={endDate} minDate={startDate} dateFormat="dd/MM/yyyy"
            customInput={<TextField label="Fecha Hasta" size="small" />} />
        </>
      )}
      {(perfil === 1 || perfil === 2) && sucursales.length > 0 && (
        <FormControl size="small" sx={{ minWidth: isMobile ? '100%' : 180 }}>
          <InputLabel>Sucursal</InputLabel>
          <Select label="Sucursal" value={sucursalId} onChange={(e) => setSucursalId(e.target.value)}>
            <MenuItem value="">Todas</MenuItem>
            {sucursales.map((s) => <MenuItem key={s.id} value={s.id}>{s.nombre}</MenuItem>)}
          </Select>
        </FormControl>
      )}
      <Button onClick={fetchTurnos} variant="contained">Buscar</Button>
    </Box>
  );

  return (
    <Container maxWidth={isMobile ? false : 'xl'} sx={{ px: isMobile ? 1 : 3 }}>
      <MenuInicial />
      <Box sx={{ my: 3 }}>
        <TituloPagina
          icon={PointOfSale}
          titulo="Turnos de caja"
          subtitulo={`${turnos.length} turno${turnos.length !== 1 ? 's' : ''} en el período`}
          ayuda="Un turno abierto es obligatorio para poder registrar ventas en esa sucursal. Al cerrarlo se manda un mail de resumen a la sucursal."
          accion={<Button variant="contained" color="primary" onClick={() => navigate('/turno')}>Abrir / cerrar turno</Button>}
        />

        {filtros}

        {loading ? (
          <Cargando />
        ) : isMobile ? (
          <Stack spacing={1.5}>
            {turnos.length === 0 && (
              <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>Sin turnos en el período.</Typography>
            )}
            {turnos.map((t) => (
              <Paper key={t.id} variant="outlined" onClick={() => navigate(`/turno?id=${t.id}`)}
                sx={{ p: 1.5, cursor: 'pointer', borderLeft: '4px solid', borderLeftColor: t.estado === 'abierto' ? 'success.main' : 'divider' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>{t.sucursal}</Typography>
                  <Chip size="small" label={t.estado === 'abierto' ? 'Abierto' : 'Cerrado'} color={t.estado === 'abierto' ? 'success' : 'default'} />
                </Box>
                <Typography variant="caption" color="text.secondary" display="block">
                  Apertura: {fmtFecha(t.fecha_apertura)} ({t.usuario_apertura})
                </Typography>
                {t.estado === 'cerrado' && perfil !== 3 && (
                  <Typography variant="caption" color="text.secondary" display="block">
                    Registrado: {fmtMoneda(t.total_ventas)}
                  </Typography>
                )}
              </Paper>
            ))}
          </Stack>
        ) : (
          <div style={{ height: 560, width: '100%' }}>
            <DataGrid
              rows={turnos} columns={columns} pageSize={10} rowsPerPageOptions={[10, 25, 50]}
              getRowId={(r) => r.id} disableSelectionOnClick
              onRowClick={(params) => navigate(`/turno?id=${params.row.id}`)}
              sx={{ '& .MuiDataGrid-row': { cursor: 'pointer' } }}
              components={{ Toolbar: ExportToolbar }}
            />
          </div>
        )}
      </Box>
    </Container>
  );
}

export default TurnosList;
